import axios from 'axios';

// Scheduler lives behind the gateway at /api/v1/scheduler/* (platform-only; the
// gateway injects the internal secret server-side). Normalise whatever shape
// VITE_API_URL is in (".../api" or ".../api/v1") to a single /api/v1 base.
const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(
  /\/api(\/v1)?\/?$/,
  '/api/v1',
);
const BASE = `${API_V1}/scheduler`;

export type ScheduleType = 'cron' | 'once' | 'interval';
export type RunStatus = 'pending' | 'published' | 'failed' | 'skipped';

export interface ScheduledJob {
  id: string;
  tenant_id: string | null;
  job_key: string;
  subject: string;
  schedule_type: ScheduleType;
  cron_expression?: string;
  interval_seconds?: number;
  timezone: string;
  payload?: unknown;
  enabled: boolean;
  max_retries: number;
  retry_delay_seconds: number;
  last_run_at?: string | null;
  next_run_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface JobRun {
  id: string;
  scheduled_job_id: string;
  tenant_id: string | null;
  subject: string;
  status: RunStatus;
  attempt: number;
  reason?: string;
  error_message?: string;
  nats_msg_id?: string;
  started_at: string;
  finished_at?: string | null;
  created_at: string;
}

/** Fields the admin UI may change on an existing job. */
export interface JobPatch {
  schedule_type?: ScheduleType;
  cron_expression?: string;
  interval_seconds?: number;
  timezone?: string;
  enabled?: boolean;
  max_retries?: number;
  retry_delay_seconds?: number;
}

export const listJobs = () =>
  axios.get<{ jobs: ScheduledJob[] }>(`${BASE}/jobs`).then((r) => r.data.jobs ?? []);

export const getJob = (id: string) =>
  axios.get<ScheduledJob>(`${BASE}/jobs/${id}`).then((r) => r.data);

export const patchJob = (id: string, patch: JobPatch) =>
  axios.patch<ScheduledJob>(`${BASE}/jobs/${id}`, patch).then((r) => r.data);

export const pauseJob = (id: string) =>
  axios.post<ScheduledJob>(`${BASE}/jobs/${id}/pause`).then((r) => r.data);

export const resumeJob = (id: string) =>
  axios.post<ScheduledJob>(`${BASE}/jobs/${id}/resume`).then((r) => r.data);

export const runNow = (id: string) =>
  axios.post<{ run: JobRun }>(`${BASE}/jobs/${id}/run-now`).then((r) => r.data.run);

export const listRuns = (id: string) =>
  axios.get<{ runs: JobRun[] }>(`${BASE}/jobs/${id}/runs`).then((r) => r.data.runs ?? []);
